import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';
import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import type { RequestError, UserAgreement } from './types';
import { AgreementType } from './types';

const logger = new Logger('useGetUserAgreement');

export function useGetUserAgreements() {
  const [agreements, setAgreements] = useState<UserAgreement[]>([]);
  const [error, setError] = useState<RequestError | null>(null);
  const [loading, setLoading] = useState(true);

  const makeRequest = useCallback(async () => {
    try {
      const endpoint = `${settings.url}/user/agreements`;

      const response = await axios.get(endpoint);
      setAgreements(response.data);

      setError(null);
    } catch (err) {
      if (isAxiosError(err) && err?.response?.status === 404) {
        setAgreements([]);
        return;
      }
      logger.error(err);
      setError(err as RequestError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (agreements.length === 0 && !error) {
      makeRequest();
    }
  }, [agreements.length, error, makeRequest]);

  const hasPrivacyPolicyAgreement = agreements.some(
    (agreement) => agreement.agreementType === AgreementType.PrivacyPolicy,
  );
  
  const hasTermsAndConditionsAgreement = agreements.some(
    (agreement) => agreement.agreementType === AgreementType.TermsAndConditions,
  );

  return {
    error,
    hasPrivacyPolicyAgreement,
    hasTermsAndConditionsAgreement,
    loading,
    setAgreements,
  };
}

export default useGetUserAgreements;
